import { useEffect, useState } from 'react';
import { PencilIcon, TrashIcon, PlusIcon } from '@bizfirst/common-react';
import type { AgentPolicyDTO, AgentPolicyFormValues } from '../../types';
import { useAgentPolicyAdminApiClient } from '../../context/AiAgentAdminApiContext';
import { ConfirmDialog } from '../Shared/ConfirmDialog';
import { UnverifiedBanner } from '../Shared/UnverifiedBanner';

const ATTRIBUTE_KEYS = [1, 2, 3, 4, 5, 6] as const;
type AttributeKey = `AppliedOnAttribute${(typeof ATTRIBUTE_KEYS)[number]}`;

const EMPTY_FORM: AgentPolicyFormValues = {
  PolicyTypeID: 0,
  ResourceTypeID: 0,
  PolicyName: '',
  PolicyDescription: '',
  AppliedOnAttribute1: '',
  AppliedOnAttribute2: '',
  AppliedOnAttribute3: '',
  AppliedOnAttribute4: '',
  AppliedOnAttribute5: '',
  AppliedOnAttribute6: '',
};

/**
 * Restored Policies tab (Phase 3 of the Agent-editor sub-entity restoration — see
 * TemplatesPanel.tsx/RulesPanel.tsx/HooksPanel.tsx, this panel's direct structural template).
 * Real entity (AIAgent_Policies) confirmed by reading Policy.cs directly: no PolicyContent/
 * IsActive; a required ResourceTypeID FK; six required AppliedOnAttribute1-6 string(300)
 * columns — all as briefed. Also required on the entity but never mentioned by the briefing:
 * PolicyTypeID (int FK), PolicyName (string, 300), PolicyDescription (string, 2000) — all three
 * are rendered here since the backend rejects a create/update without them. No lookup
 * controllers were found for PolicyTypeID/ResourceTypeID, so both render as plain required
 * number inputs, same treatment RulesPanel.tsx gives AgentRuleTypeID/TriggerEventID.
 */
export function PoliciesPanel({ agentId }: { agentId: number }) {
  const api = useAgentPolicyAdminApiClient();
  const [policies, setPolicies] = useState<AgentPolicyDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<AgentPolicyDTO | 'new' | null>(null);
  const [form, setForm] = useState<AgentPolicyFormValues>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AgentPolicyDTO | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    setLoading(true);
    const result = await api.getPoliciesByAgent(agentId);
    setPolicies(result.data ?? []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  const startEdit = (policy: AgentPolicyDTO | 'new') => {
    setEditing(policy);
    setError(null);
    if (policy === 'new') {
      setForm(EMPTY_FORM);
      return;
    }
    const next: AgentPolicyFormValues = {
      PolicyTypeID: policy.PolicyTypeID,
      ResourceTypeID: policy.ResourceTypeID,
      PolicyName: policy.PolicyName,
      PolicyDescription: policy.PolicyDescription,
      AppliedOnAttribute1: policy.AppliedOnAttribute1,
      AppliedOnAttribute2: policy.AppliedOnAttribute2,
      AppliedOnAttribute3: policy.AppliedOnAttribute3,
      AppliedOnAttribute4: policy.AppliedOnAttribute4,
      AppliedOnAttribute5: policy.AppliedOnAttribute5,
      AppliedOnAttribute6: policy.AppliedOnAttribute6,
    };
    setForm(next);
  };

  const handleSave = async () => {
    if (!form.PolicyName.trim() || !form.PolicyDescription.trim() || !form.PolicyTypeID || !form.ResourceTypeID) {
      setError('Policy name, description, policy type and resource type are required.');
      return;
    }
    setSaving(true);
    setError(null);
    const result = editing === 'new' ? await api.createPolicy(agentId, form) : await api.updatePolicy((editing as AgentPolicyDTO).AgentPolicyID, form);
    setSaving(false);
    if (result.success) {
      setEditing(null);
      await load();
    } else {
      setError(result.error?.message ?? 'Failed to save.');
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    const result = await api.deletePolicy(pendingDelete.AgentPolicyID);
    setDeleting(false);
    if (result.success) {
      setPendingDelete(null);
      setPolicies((prev) => prev.filter((p) => p.AgentPolicyID !== pendingDelete.AgentPolicyID));
    }
  };

  if (editing) {
    return (
      <div className="aaa-form-grid">
        <UnverifiedBanner entityName="Policies" />
        <div className="aaa-form-group aaa-form-group-full">
          <label>Policy Name *</label>
          <input type="text" value={form.PolicyName} onChange={(e) => setForm((f) => ({ ...f, PolicyName: e.target.value }))} />
        </div>
        <div className="aaa-form-group aaa-form-group-full">
          <label>Policy Description *</label>
          <textarea rows={3} value={form.PolicyDescription} onChange={(e) => setForm((f) => ({ ...f, PolicyDescription: e.target.value }))} />
        </div>
        <div className="aaa-form-group">
          <label>Policy Type ID *</label>
          <input
            type="number"
            value={form.PolicyTypeID || ''}
            onChange={(e) => setForm((f) => ({ ...f, PolicyTypeID: e.target.value ? parseInt(e.target.value, 10) : 0 }))}
          />
        </div>
        <div className="aaa-form-group">
          <label>Resource Type ID *</label>
          <input
            type="number"
            value={form.ResourceTypeID || ''}
            onChange={(e) => setForm((f) => ({ ...f, ResourceTypeID: e.target.value ? parseInt(e.target.value, 10) : 0 }))}
          />
        </div>

        <fieldset className="aaa-form-group-full aaa-fieldset">
          <legend>Applied On Attributes (all required)</legend>
          <div className="aaa-form-grid">
            {ATTRIBUTE_KEYS.map((n) => {
              const key = `AppliedOnAttribute${n}` as AttributeKey;
              return (
                <div className="aaa-form-group" key={key}>
                  <label>Attribute {n} *</label>
                  <input type="text" value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} />
                </div>
              );
            })}
          </div>
        </fieldset>

        {error && <div className="aaa-editor-error aaa-form-group-full">{error}</div>}
        <div className="aaa-form-group-full aaa-editor-footer-actions">
          <button type="button" className="aaa-btn aaa-btn-default" onClick={() => setEditing(null)} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="aaa-btn aaa-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Policy'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <UnverifiedBanner entityName="Policies" />
      <div className="aaa-editor-footer-actions" style={{ marginBottom: 12 }}>
        <button type="button" className="aaa-btn aaa-btn-primary aaa-btn-sm" onClick={() => startEdit('new')}>
          <PlusIcon size={13} /> Add Policy
        </button>
      </div>
      {loading && <div className="aaa-editor-loading">Loading…</div>}
      {!loading && policies.length === 0 && <div className="aaa-entity-dropdown-empty">No policies yet.</div>}
      <div className="aaa-entity-grid">
        {policies.map((policy) => (
          <div key={policy.AgentPolicyID} className="aaa-entity-card">
            <div className="aaa-entity-card-header">
              <strong>{policy.PolicyName}</strong>
            </div>
            <p>{policy.PolicyDescription}</p>
            <div className="aaa-entity-card-actions">
              <button type="button" className="aaa-icon-btn" onClick={() => startEdit(policy)} title="Edit">
                <PencilIcon size={13} />
              </button>
              <button type="button" className="aaa-icon-btn aaa-icon-btn-danger" onClick={() => setPendingDelete(policy)} title="Delete">
                <TrashIcon size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        title="Delete Policy"
        message={`Delete policy "${pendingDelete?.PolicyName}"?`}
        busy={deleting}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
